import { buildCartItemOptionsPayload } from "@/lib/build-checkout-cart-items"
import { findCategoryForOffer } from "@/lib/find-category-for-offer"
import type { Cart, CartItem, Category } from "@/types"
import type { CartLinePayload, CartOptionPayload } from "./types"

function toCents(value: number | undefined): number {
  return Math.round((value ?? 0) * 100)
}

/** Catalog item id of a line — the order item's `id` (MerchantItemId). */
export function catalogItemIdOf(line: CartItem): string {
  return line.item?.id || line.itemOffer?.itemId || ""
}

/** The `lineId` of the cart line at `index`: its position in `order.items` (C6.1). */
export function lineIdAt(index: number): string {
  return String(index)
}

/** The cart line a `lineId` points at, or undefined when it is not a position of this cart. */
export function cartLineFor(cart: Cart, lineId: string): CartItem | undefined {
  if (!/^\d+$/.test(lineId)) return undefined
  return cart.items[Number(lineId)]
}

function optionsOf(line: CartItem, index: number): CartOptionPayload[] {
  if (!line.optionQuantities || !line.optionGroups) return []
  return buildCartItemOptionsPayload(line).map((option) => {
    const linear = Number.isInteger(option.quantity) && option.quantity > 0
    const unitCents = toCents(option.unitPrice.value)
    const subtotalCents = toCents(option.subtotalPrice.value)
    const exact = linear && unitCents * option.quantity === subtotalCents
    return {
      id: `${lineIdAt(index)}:${option.optionGroupId}:${option.optionId}`,
      optionId: option.optionId,
      externalCode: option.externalCode,
      name: option.name,
      quantity: exact ? option.quantity : 1,
      unitPriceCents: exact ? unitCents : subtotalCents,
    }
  })
}

/**
 * The cart as the discount evaluators read it, in integer cents, one entry per
 * bag line in bag order — the same order checkout sends `items[]`, so each
 * `lineId` is the position the order-service stamps as `Item.index`.
 *
 * The base unit price absorbs any rounding left between the options and the
 * unit price the customer sees, so `quantity × (base + options)` always equals
 * what the line costs. A fractional quantity (weighed items) travels as one
 * unit priced at the whole line, since the wire quantity is an integer.
 * `benefitCents[i]` is the LOYALTY_* discount already on line `i` (C1.1).
 */
export function buildEvaluationLines(
  cart: Cart,
  categories: Category[],
  benefitCents: readonly number[] = [],
): CartLinePayload[] {
  return cart.items.map((line, index) => {
    const common = {
      lineId: lineIdAt(index),
      itemId: catalogItemIdOf(line),
      externalCode: line.item?.externalCode ?? "",
      categoryId: findCategoryForOffer(categories, line.itemOffer?.id ?? "")?.id ?? "",
      name: line.item?.name ?? "",
      benefitDiscountCents: benefitCents[index] ?? 0,
    }
    if (!Number.isInteger(line.quantity) || line.quantity <= 0) {
      return { ...common, quantity: 1, unitPriceCents: toCents(line.itemTotalPrice), options: [] }
    }
    const options = optionsOf(line, index)
    const optionsCents = options.reduce((sum, option) => sum + option.quantity * option.unitPriceCents, 0)
    return {
      ...common,
      quantity: line.quantity,
      unitPriceCents: toCents(line.unitPrice) - optionsCents,
      options,
    }
  })
}
